<template>
  <v-dialog
    v-model="modal"
    :width="width"
    @keydown.escape="cancel"
    v-bind="{ ...$props, ...$attrs }">
    <template #activator="slotProps" v-if="!externalActivator">
      <slot name="activator" v-bind="slotProps" v-if="$slots.activator" />
    </template>
    <v-card class="dialog-box">
      <v-card-title class="d-flex flex-row">
        <span class="text-h5 d-flex">
          {{ t('carrier.dialogs.carrierContactSubscriberDialog.headers.manageSubscribers') }}
        </span>
        <v-spacer></v-spacer>
        <v-btn class="pa-3" variant="text" icon="mdi-close" @click="cancel"></v-btn>
      </v-card-title>
      <v-card-text class="dialog-body">
        <carrier-contact-card
          :carrier="carrierContact.user"
          :company="carrierContact.company"
          :is-favorite="carrierContact.isFavorite" />
        <div class="subscribers-wrapper">
          <label for="carrier-contact-subscribers">
            {{
              t(
                'carrier.dialogs.carrierContactSubscriberDialog.labels.sendEmailNotificationsForAllAppointmentsScheduledWithThisContactTo'
              )
            }}:
          </label>
          <email-list-field
            ref="emailField"
            :loading="isLoadingRef"
            v-model="subscribers"
            id="carrier-contact-subscribers"
            name="carrier-contact-subscribers"
            class="carrier-contact-subscribers"
            data-testid="carrier-contact-subscribers" />
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="dialog-actions">
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="cancel" data-testid="carrier-contact-subscribers-nevermind">
          {{ t('global.buttons.nevermind') }}
        </v-btn>
        <v-btn
          color="primary"
          @click="save"
          :disabled="!canSave"
          data-testid="carrier-contact-subscribers-save">
          {{ t('carrier.dialogs.carrierContactSubscriberDialog.buttons.saveSubscribers') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { defineComponent, ref, computed, nextTick } from 'vue';
import useModal from '@/composables/useModal';
import useCarrierContacts from '@/composables/useCarrierContacts';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'CarrierContactSubscriberDialog',
  props: {
    width: { type: Number, default: 550 },
    externalActivator: { type: Boolean, default: false },
    carrierContact: { type: Object, required: true }
  },
  setup(props, context) {
    const { t } = useI18n();
    const store = useStore();
    const emailField = ref();
    const subscribers = ref(props.carrierContact?.emailCCs || []);
    const canSave = computed(() => emailField.value?.errorMessage === null);
    const { modal, closeModal, openModal } = useModal();
    const { update, isLoadingRef } = useCarrierContacts();

    const save = async () => {
      emailField.value?.validate?.();
      await nextTick();

      if (!canSave.value) {
        return;
      }

      try {
        await update(
          { id: props.carrierContact.id, emailCCs: subscribers.value },
          { skipNotify: true }
        );
        store.notify(
          t(
            'carrier.dialogs.carrierContactSubscriberDialog.messages.subscribersUpdatedSuccessfully'
          ),
          'success'
        );
        closeModal();
      } catch (error) {
        console.error(
          t('carrier.dialogs.carrierContactSubscriberDialog.messages.failedToUpdateSubscribers'),
          error
        );
      }
    };

    const cancel = () => {
      // reset subscribers to original value
      subscribers.value = props.carrierContact?.emailCCs || [];
      closeModal();
    };

    context.expose({ openModal, closeModal });
    return { t, modal, closeModal, subscribers, canSave, save, emailField, isLoadingRef, cancel };
  }
});
</script>

<style scoped lang="scss">
.dialog-body {
  margin: 40px 0;
  padding-bottom: 0 !important;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 32px;
}

.dialog-actions {
  padding: 16px !important;
}

.subscribers-wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 32px 0 48px 0;
}
</style>
